import React, { FC, useCallback } from "react";
import { observer } from "mobx-react";
import { LeadSourceTag } from "components";
import { ExtendedColumnInstance } from "components/table/table-props";
import { LeadSourceEnum } from "enums/lead-source.enum";
import { GrsPendingLeadPrompt } from "routes/dashboard/components/prompts/GrsPendingLeadPrompt/GrsPendingLeadPrompt";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { TGrsPendingLead } from "types/grs-pending-lead.type";
import { TLead } from "types/lead.type";
import { useActionPrompt } from "utils/react-hooks/useActionPrompt.hook";
import { GrsPendingLeadRow } from "./GrsPendingLeadRow";
import { GrsPendingLeadsShowMoreRow } from "./GrsPendingLeadsShowMoreRow";

interface IGrsPendingLeadRowsProps {
  accountStore: UserAccountStore;
  columns: ExtendedColumnInstance<TLead>[];
}

export const GrsPendingLeadRows: FC<IGrsPendingLeadRowsProps> = observer(
  ({ accountStore, columns }) => {
    const { setModal, unSetModal } = useActionPrompt();
    const {
      accountGrsPendingLeadsStore,
      accountLeadSourcesStore: { leadSourcesMapBySource },
      portalsMap,
      account: { logoUrl },
    } = accountStore;
    const {
      visiblePendingLeads,
      pendingLeadsCount,
      hiddenPendingLeadsCount,
      nextRevealCount,
      showMore,
    } = accountGrsPendingLeadsStore;

    const closePrompt = useCallback(() => unSetModal(), [unSetModal]);

    const openPendingLeadPrompt = useCallback(
      ({ id }: TGrsPendingLead) => {
        setModal(
          <GrsPendingLeadPrompt
            pendingLeadId={id}
            accountGrsPendingLeadsStore={accountGrsPendingLeadsStore}
            closePrompt={closePrompt}
          />,
          { size: "4xl" }
        );
      },
      [accountGrsPendingLeadsStore, setModal, closePrompt]
    );

    if (!pendingLeadsCount) return null;

    const sourceTag = (
      <LeadSourceTag
        source={LeadSourceEnum.nurtur_grs}
        accountLogoUrl={logoUrl}
        leadSourcesMapBySource={leadSourcesMapBySource}
        portalsMap={portalsMap}
      />
    );

    return (
      <>
        {visiblePendingLeads.map(pendingLead => (
          <GrsPendingLeadRow
            key={pendingLead.id}
            pendingLead={pendingLead}
            columns={columns}
            sourceTag={sourceTag}
            onAction={openPendingLeadPrompt}
          />
        ))}
        {hiddenPendingLeadsCount > 0 && (
          <GrsPendingLeadsShowMoreRow
            total={pendingLeadsCount}
            nextRevealCount={nextRevealCount}
            onShowMore={showMore}
          />
        )}
      </>
    );
  }
);
